import type { RefObject } from 'react';
import type { SkillLevelDto, SkillSessionDto } from '../api/skills';
import { AnticipationRing } from '../components/AnticipationRing';
import { sessionElapsedFraction } from '../components/SkillBusyCard';

export type SkillsLineProps = {
  /** The companion's skills in the server's S3 order; `undefined` while the query loads. */
  skills?: SkillLevelDto[];
  /** `sessions[0]` — busy is the row's existence, never a clock compare (the server owns "done"). */
  session?: SkillSessionDto;
  now: Date;
  /** Opens `SkillsSheet`. */
  onOpen: () => void;
  /** The `Skills ›` button — the sheet's `restoreFocusRef`, so a close returns focus here. */
  triggerRef?: RefObject<HTMLButtonElement>;
};

/**
 * The top two skills by level, S3 order as the tiebreak. `Array.prototype.sort` is stable
 * (ES2019), so a level-desc sort over the server's own S3-ordered list leaves ties exactly where
 * the server put them — Woodcutting 2 · Mining 1 · Smithing 1 … reads "Woodcutting 2 · Mining 1",
 * and no client-side notion of skill order exists to drift from the pack's `sort` field.
 */
function topTwo(skills: SkillLevelDto[]): SkillLevelDto[] {
  return [...skills].sort((a, b) => b.level - a.level).slice(0, 2);
}

/** `~2m` on the line (compact; the sheet's card still says `~2 min`), `Finishing up…` once past
 *  `completesAt` — the same rule `SkillBusyCard` applies, since the 15 s sweep may already have
 *  applied the row and a negative ETA would be a lie. Minutes at finest, floored at 1 (C3). */
function lineEta(session: SkillSessionDto, now: Date): string {
  const remaining = Date.parse(session.completesAt) - now.getTime();
  if (remaining <= 0) return 'Finishing up…';
  return `~${Math.max(1, Math.round(remaining / 60_000))}m`;
}

/**
 * THE HABITAT'S ONE SKILLS ROW (skill-craft UI polish, R2 / spec §3.8). The eight-chip
 * `SkillsRoster` grew the identity bar until the action dock covered it; this is one flex row in
 * its place — under the care meters on the phone/tablet, under the stat chips in the desk's
 * `IdentityPanel` — and the sheet it opens is the roster.
 *
 * Busy: a mini ring (the card's own `sessionElapsedFraction`, drawn at `--sp-5`), the server's
 * `busyLabel`, a coarse ETA. NO Cancel here — that verb lives on the sheet's full card, where it
 * has room and a place name beside it. Idle: `Skills · <top two>`. Either way the row ends in a
 * `Skills ›` quiet button. Presentational: `HabitatScreen` owns the query (constraint 5), and the
 * row renders even while `skills` is still loading so the bar's height does not jump on arrival.
 */
export function SkillsLine({ skills, session, now, onOpen, triggerRef }: SkillsLineProps) {
  // Busy: two spans, so the label ellipsises and the ETA — the one part that changes — never does
  // (the single space between them is real text for the accessible string; the flex gap draws it).
  const text = session ? (
    <>
      <span className="skills-line-label">{session.busyLabel || session.actionName || session.actionCode}</span>
      {' '}
      <span className="skills-line-eta">{`· ${lineEta(session, now)}`}</span>
    </>
  ) : (
    // Idle: ALSO a `.skills-line-label` span, never a bare text node. `.skills-line-text` is
    // `display: flex`, and `text-overflow: ellipsis` only applies to a block container's own
    // inline content — an anonymous flex item gets no ellipsis, so the bare string hard-clipped
    // "Skills · Woodcutting 12 · Herblore 10" at 375 wide (final review wave, 2026-09-17).
    // NBSP between name and level: where the line is allowed to wrap (the desk panel), the pair
    // stays together — `Woodcutting / 1` reads as a number that lost its label.
    <span className="skills-line-label">
      {['Skills', ...topTwo(skills ?? []).map((s) => `${s.name}\u00a0${s.level}`)].join(' · ')}
    </span>
  );
  return (
    <div className={`skills-line${session ? ' skills-line-busy' : ''}`}>
      {session && (
        <AnticipationRing fraction={sessionElapsedFraction(session, now)} size="sm" />
      )}
      <span className="skills-line-text">{text}</span>
      <button
        ref={triggerRef}
        type="button"
        className="btn-quiet skills-line-open"
        aria-haspopup="dialog"
        onClick={onOpen}
      >
        Skills ›
      </button>
    </div>
  );
}
